// Browser implementation of React Native's `Alert`.
//
// react-native-web exports `Alert` as a no-op, and 352 files in the app call
// `Alert.alert(...)` for every user-facing error and confirmation. This module
// provides a drop-in `Alert` object (wired in via the `react-native` alias in
// rspack.config.js) backed by a tiny queue, plus `<WebAlertHost/>` which
// renders the head of the queue as a centred dialog styled to the app's
// design system (docs/DESIGN_SYSTEM.md): navy title, teal primary action,
// `#E85C5C` destructive action, pill buttons.

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native-web';

export type AlertButtonStyle = 'default' | 'cancel' | 'destructive';

export interface AlertButton {
  text?: string;
  onPress?: (value?: string) => void;
  style?: AlertButtonStyle;
  isPreferred?: boolean;
}

export interface AlertOptions {
  cancelable?: boolean;
  onDismiss?: () => void;
}

interface PendingAlert {
  id: number;
  title: string;
  message?: string;
  buttons: AlertButton[];
  options?: AlertOptions;
}

type Listener = (queue: PendingAlert[]) => void;

let queue: PendingAlert[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((l) => l(queue));
}

function enqueue(alert: Omit<PendingAlert, 'id'>): void {
  // A screen that re-fetches on focus can raise the same alert several
  // times before the user dismisses the first. Native shows them one after
  // another too, but a stack of identical dialogs reads as a bug; keep one.
  const duplicate = queue.some(
    (q) => q.title === alert.title && q.message === alert.message,
  );
  if (duplicate) return;
  queue = [...queue, { ...alert, id: nextId++ }];
  emit();
}

function dismiss(id: number): void {
  queue = queue.filter((a) => a.id !== id);
  emit();
}

export const Alert = {
  alert(
    title: string,
    message?: string,
    buttons?: AlertButton[],
    options?: AlertOptions,
  ): void {
    const resolved =
      buttons && buttons.length > 0 ? buttons : [{ text: 'OK', style: 'default' as const }];
    enqueue({ title: title ?? '', message, buttons: resolved, options });
  },
  // Android-only API on mobile; provided for signature parity.
  prompt(
    title: string,
    message?: string,
    callbackOrButtons?: ((text: string) => void) | AlertButton[],
  ): void {
    // eslint-disable-next-line no-alert
    const value = window.prompt([title, message].filter(Boolean).join('\n'));
    if (typeof callbackOrButtons === 'function') {
      if (value !== null) callbackOrButtons(value);
      return;
    }
    const buttons = callbackOrButtons ?? [];
    const target =
      value === null
        ? buttons.find((b) => b.style === 'cancel')
        : buttons.find((b) => b.style !== 'cancel') ?? buttons[0];
    target?.onPress?.(value ?? undefined);
  },
};

export function WebAlertHost(): React.JSX.Element | null {
  const [current, setCurrent] = useState<PendingAlert | null>(queue[0] ?? null);

  useEffect(() => {
    const listener: Listener = (q) => setCurrent(q[0] ?? null);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const cancel = current.buttons.find((b) => b.style === 'cancel');
      if (cancel || current.options?.cancelable) {
        dismiss(current.id);
        cancel?.onPress?.();
        current.options?.onDismiss?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current]);

  if (!current) return null;

  const press = (button: AlertButton) => {
    dismiss(current.id);
    // Defer so the dialog is gone before any navigation the handler triggers.
    setTimeout(() => button.onPress?.(), 0);
  };

  const stacked = current.buttons.length > 2;

  return (
    <Modal transparent visible animationType="fade" onRequestClose={() => undefined}>
      <View style={styles.backdrop} accessibilityRole="none">
        <View
          style={styles.card}
          accessibilityRole="alert"
          accessibilityLabel={current.title}
        >
          <Text style={styles.title}>{current.title}</Text>
          {current.message ? <Text style={styles.message}>{current.message}</Text> : null}
          <View style={[styles.buttons, stacked && styles.buttonsStacked]}>
            {current.buttons.map((b, i) => {
              const isCancel = b.style === 'cancel';
              const isDestructive = b.style === 'destructive';
              return (
                <Pressable
                  key={`${b.text ?? 'btn'}-${i}`}
                  onPress={() => press(b)}
                  accessibilityRole="button"
                  accessibilityLabel={b.text ?? 'OK'}
                  style={({ pressed }: { pressed: boolean }) => [
                    styles.button,
                    isCancel && styles.buttonCancel,
                    isDestructive && styles.buttonDestructive,
                    stacked && styles.buttonStacked,
                    pressed && styles.buttonPressed,
                  ]}
                >
                  <Text style={[styles.buttonText, isCancel && styles.buttonTextCancel]}>
                    {b.text ?? 'OK'}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28, 44, 79, 0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 22,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: 8,
  },
  message: {
    fontSize: 15,
    lineHeight: 21,
    color: '#2C4364',
    textAlign: 'center',
    marginBottom: 6,
  },
  buttons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 14,
  },
  buttonsStacked: {
    flexDirection: 'column',
  },
  button: {
    flex: 1,
    minHeight: 44,
    borderRadius: 25,
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  buttonStacked: {
    flex: undefined,
    width: '100%',
  },
  buttonCancel: {
    backgroundColor: '#F3F6F8',
    borderWidth: 1,
    borderColor: '#D8DBDD',
  },
  buttonDestructive: {
    backgroundColor: '#E85C5C',
  },
  buttonPressed: {
    opacity: 0.85,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  buttonTextCancel: {
    color: '#1C2C4F',
  },
});
